import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url, USER_PROFILE_SECTION_PATHS } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiContextProvidedValue,
  BiData,
  BuyCoreButtonBiEvent,
  ViewShopPremiumDownloadsTabBiEvent,
  ViewSubscriptionTabBiEvent,
} from '@wix/da-bi/pkg/events';
import PromoBadge from '@wix/da-ds/pkg/Badges/PromoBadge';
import {
  BannerButton,
  BannerLayout,
  BannerMessage,
} from '@wix/da-ds/pkg/Banner/lego';
import BuyCoreTextButton from '../../Button/core/dsPresets/BuyCoreTextButton';
import { getIsAdoptableOfferType } from '../../utils/adoptables';
import OfferBannerMobile from './OfferBannerMobile';
import OfferBanner from './OfferBanner';
import s from './MonetizationBanner.scss';

interface Props {
  className?: string;
  deviation: PapiDeviation;
  onAdoptableSaleEnd?: () => void;
}

/**
 * DS7.4.1, but the monetization variant.
 *
 * Includes some adoptables one-offs that aren't related to DS7.4.1. We should probably refactor those out.
 */
const MonetizationBanner: React.FC<Props> = ({
  className,
  deviation,
  onAdoptableSaleEnd,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isDesktop = !isMobile;
  const isAdoptable = !!deviation.extended?.adoptable;
  const offers = deviation.extended?.adoptable?.offers;
  const promo = deviation?.extended?.showStageMonetizationPromo;

  if (
    isAdoptable &&
    // Adoptables that aren't on sale should show the monetization banners as usual.
    deviation.adoptable?.status !== 'not_onsale'
  ) {
    if (
      isDesktop ||
      !deviation.adoptable ||
      !getIsAdoptableOfferType(deviation)
    ) {
      return null;
    }

    if (isMobile) {
      return null;
    }

    if (deviation.adoptable.status === 'sold') {
      return null;
    }

    return (
      <OfferBannerMobile
        className={className}
        {...offers}
        ownOffer={offers?.ownOffer}
        deviation={deviation}
      />
    );
  }

  const showOfferBanner =
    offers &&
    (deviation.adoptable?.status === 'onsale' ||
      deviation.adoptable?.status === 'pending');

  if (showOfferBanner) {
    return (
      <OfferBanner
        {...offers}
        className={className}
        ownOffer={offers?.ownOffer}
        deviation={deviation}
        onEnd={onAdoptableSaleEnd}
      />
    );
  }

  // dont show Monetization Strip on mobile
  if (isMobile) {
    return null;
  }

  if (
    !promo ||
    ![
      'tier',
      'commission',
      'premium_gallery',
      'adoptable',
      'core',
      'generic',
    ].includes(promo) ||
    !deviation.author
  ) {
    return null;
  }

  let link: string;
  let biData;
  const commonBiData = {
    evid: 375,
    typeid: deviation.typeId,
    itemid: deviation.deviationId,
    referral_info: ReferralType.ART_STAGE_PROMOTION,
    section_typeid: deviation.typeId,
  };

  if (promo === 'tier') {
    biData = BiData<ViewSubscriptionTabBiEvent>({
      sectionname: BiContextProvidedValue,
      ...commonBiData,
      evid: 547,
    });
    link = Url.userSubscriptionsPath({
      username: deviation.author.username,
      anchor: true,
    });
  } else if (promo === 'core') {
    link = Url.coreMembershipLink();
    biData = BiData<BuyCoreButtonBiEvent>({
      ...commonBiData,
      evid: 375,
    });
  } else {
    biData = BiData<ViewShopPremiumDownloadsTabBiEvent>({
      ...commonBiData,
      evid: 557,
    });
    link = Url.userLink(
      deviation.author.username,
      USER_PROFILE_SECTION_PATHS.SHOP,
      true
    );
  }

  // translation keys:
  // deviationview.artstage.monetizationStrip.var.tier.body
  // deviationview.artstage.monetizationStrip.var.tier.CTA
  // deviationview.artstage.monetizationStrip.var.core.body
  // deviationview.artstage.monetizationStrip.var.core.CTA
  // deviationview.artstage.monetizationStrip.var.generic.body
  // deviationview.artstage.monetizationStrip.var.generic.CTA

  const tPrefix = 'deviationview.artstage.monetizationStrip';
  let keyPrefix;
  if (promo === 'tier') {
    keyPrefix = `${tPrefix}.var.tier`;
  } else if (promo === 'core') {
    keyPrefix = `${tPrefix}.var.core`;
  } else {
    keyPrefix = `${tPrefix}.var.generic`;
  }

  return (
    <BannerLayout
      size="small"
      className={c(
        className,
        promo === 'core' ? s['variant-upsell'] : s['variant-subscription']
      )}
      body={[
        promo !== 'core' && (
          <PromoBadge
            key="badge"
            badgeType={promo === 'tier' ? 'tier' : 'generic'}
            size={16}
          />
        ),
        <BannerMessage key="message" weight="reg">
          {t(`${keyPrefix}.body`)}
        </BannerMessage>,
      ]}
      postfix={
        promo === 'core' ? (
          <BuyCoreTextButton biData={commonBiData} size="small">
            {t(`${keyPrefix}.CTA`)}
          </BuyCoreTextButton>
        ) : (
          <BannerButton href={link} biData={biData} variant="underline">
            {t(`${keyPrefix}.CTA`)}
          </BannerButton>
        )
      }
    />
  );
};

MonetizationBanner.displayName = 'MonetizationBanner';

export default MonetizationBanner;
